import { alpha, Box, Button, darken, useTheme } from '@mui/material';
import type { SystemStyleObject } from '@mui/system';
import { DateRange } from '@mui/x-date-pickers-pro';
import { TZDate } from '@repo/tzdate';
import { createRef, useContext, useState } from 'react';
import { Dayjs } from 'dayjs';
import { convertDateRangeToTZDate } from 'src/utils/dateRangeUtils';
import Popover from 'src/components/common/popover/Popover';
import { useFocusTrap } from 'src/hooks/common/useFocusTrap';
import { useLocale } from '@repo/i18n';
import { MountPointContext } from '@repo/common-utils/mountPoint';
import { capitalize } from '@repo/common-utils/TextUtils';
import { useCustomizations } from 'src/components/utils/theme/customizations';
import { zIndex } from 'src/utils/widget/zIndex';
import StaticDateRangePicker from '../StaticDateRangePicker';
import { useConfigurations } from '@repo/widget-utils/widgetsConfiguration';

type IProps = {
    dateRangeVariant?: 'days' | 'nights' | undefined;
    anchorEl: HTMLInputElement | HTMLDivElement | null;
    setAnchorEl: React.Dispatch<React.SetStateAction<HTMLDivElement | HTMLInputElement | null>>;
    minDate: TZDate | undefined;
    onSelectDateRange: (dateRange: DateRange<TZDate>) => void;
    dateRange: DateRange<TZDate>;
};

export default function DateRangePickerPopover(props: IProps) {
    const { anchorEl, setAnchorEl, minDate, onSelectDateRange, dateRange, dateRangeVariant } =
        props;
    const mountPoint = useContext(MountPointContext);
    const popoverRef = createRef<HTMLDivElement>();
    const theme = useTheme();
    const customizations = useCustomizations();
    const { t } = useLocale();
    const config = useConfigurations();
    const showWeekNumbers = config.showWeekNumbers ?? false;
    const trap = useFocusTrap(popoverRef, !!anchorEl, '#bilberry-date-range-picker-input');
    const [displayDateRange, setDisplayDateRange] = useState(dateRange);
    const onClose = () => {
        if (trap) trap.deactivate();
        setAnchorEl(null);
    };

    const disableOkButton = dateRangeVariant === 'nights' && displayDateRange?.includes(null);

    const onOkClicked = () => {
        onSelectDateRange(displayDateRange);
        onClose();
    };

    const handleDateRangeChange = (newDateRange: DateRange<Dayjs | TZDate>, _context?: any) => {
        setDisplayDateRange(convertDateRangeToTZDate(newDateRange));
    };

    return (
        <Popover
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            sx={
                {
                    zIndex: `${zIndex.alwaysVisible} !important`,

                    '& > .MuiPaper-root': {
                        maxWidth: showWeekNumbers ? 365 : 325,
                    },
                } as SystemStyleObject
            }
            container={mountPoint.popover}
            anchorOrigin={{ vertical: 'center', horizontal: 'center' }}
            transformOrigin={{ vertical: 'center', horizontal: 'center' }}
            onClose={onClose}
            disableEnforceFocus={true}
            ref={popoverRef}
        >
            <StaticDateRangePicker
                dateRange={displayDateRange}
                onChange={handleDateRangeChange}
                maxDate={TZDate.now().add(5, 'years')}
                minDate={minDate}
                showWeekNumbers={showWeekNumbers}
                weekNumberTopPadding={96}
            />
            <Box
                p={theme.spacing(0, 0, 2, 0)}
                display="flex"
                justifyContent="center"
                sx={{
                    '& button:hover': {
                        backgroundColor: () =>
                            customizations.primaryButtonStyle === 'contained'
                                ? darken(theme.palette.primary.main, 0.2)
                                : alpha(theme.palette.common.black, 0.3),
                    },
                }}
            >
                <Button
                    variant={customizations.primaryButtonStyle}
                    color="primary"
                    onClick={onOkClicked}
                    disabled={disableOkButton}
                    role={capitalize(t.select_date)}
                >
                    {t.ok}
                </Button>
            </Box>
        </Popover>
    );
}
